/* eslint-disable max-len */
import { useQuery } from '@tanstack/react-query';
import { useExperiment } from '@acp/feature/auth';
import { useMemo } from 'react';
import { BookableServiceV2, Experiment } from '@acp/shared';
import { getAvailableOasTimes, getBookableServicesByPropertyIdV2 } from '../service';

function useBookableServices({
    propertyId,
    isEnabled = true,
    overrideBookableServices = null,
}: {
    propertyId: number;
    isEnabled?: boolean;
    overrideBookableServices?: BookableServiceV2[] | null;
}) {
    const { hasExperiment } = useExperiment();

    // check if the user has the experiment
    const isOasBookableServicesExperimentEnabled = hasExperiment(Experiment.OverrideBookableServices);
    // set up a flag for checking if the override bookable services has data and experiment
    const isOasBookableServicesEnabled = overrideBookableServices !== null && isOasBookableServicesExperimentEnabled;

    // if above flag is true, disable the query that finds bookable services in admin system
    // and trust the bookable services that were passed in from access issues API
    const query = useQuery({
        queryKey: ['property', propertyId, 'booking', 'bookable-services'],
        queryFn: () => getBookableServicesByPropertyIdV2(propertyId),
        enabled: isOasBookableServicesEnabled ? false : isEnabled,
    });

    const [isLoading, data] = useMemo(() => {
        if (isOasBookableServicesEnabled === false) {
            return [query.isLoading, query.data];
        }

        return [false, overrideBookableServices];
    }, [
        query,
        overrideBookableServices,
        isOasBookableServicesEnabled,
    ]);

    return { isLoading, data };
}

function useAvailableOasTimes({
    propertyId,
    service,
}: {
    propertyId: number;
    service: string;
}) {
    const query = useQuery({
        queryKey: ['property', propertyId, 'booking', 'availability', service],
        queryFn: service !== undefined ? () => getAvailableOasTimes(propertyId, service) : undefined,
    });

    return query;
}

export { useBookableServices, useAvailableOasTimes };
